import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

const STORY = {
  en: {
    eyebrow: "A circle completed",
    title: "The Wreath",
    sub: "Some things we keep until the story they belong to is ready to close.",
    imageCaption: "The wreath, with details from its circle.",
    cta: "Create together",
    imageAlt: "The wreath of twigs and rowan berries with a half-painted antler, and close-ups of the grasshopper, painted folk flowers and the shell vial",
    blocks: [
      { t: "p", x: "This wreath is made from fragments of a chapter of my own life." },
      {
        t: "p",
        x: "The cattail carries the memory of Lucy — the cat who came into my life during my marriage. A small, almost ordinary thing, but to me it holds an entire time, a home, a relationship, and the life that existed around it.",
      },
      {
        t: "p",
        x: "The antler I found in Finnmark in 2023.\nI could not have known then what it would eventually become part of. But that journey holds a very particular place in my story. It was there that I first understood — not as a thought, but as something much deeper — that my marriage was going to end. I remember it almost as a message arriving before I was ready to act upon it.",
      },
      { t: "p", x: "I carried the antler home.\nYears later, I painted it and gave it a place within this piece." },
      {
        t: "p",
        x: "And then there is the large green grasshopper — a symbol that came into my life carrying its own meaning of change, movement and the courage to move forward.",
      },
      { t: "pull", x: "Individually, these fragments hold different moments.\nTogether, inside the circle of the wreath, they tell one story." },
      { t: "img" },
      {
        t: "p",
        x: "A relationship.\nA life lived within it.\nThe first knowing that it would end.\nThe transformation that followed.\nAnd eventually, the closing of the circle.",
      },
      { t: "p", x: "Perhaps that is why the wreath had to be a circle." },
      {
        t: "p",
        x: "Not because the past disappears, and not because everything that belonged to it needs to be discarded.\nSome things are allowed to remain.\nBut they can be gathered differently.\nRearranged.\nReinterpreted.\nGiven another form.",
      },
      { t: "p", x: "The rowan berries bring another meaning into the circle.\nRowan has long been associated with protection — with guarding the home, the threshold and those passing through times of change. In this piece, I see the berries as something more personal too." },
      { t: "p", x: "They are the living part of the wreath.\nSmall, bright seeds held amongst fragments of a life that has already been lived. To me, they have come to represent what remains after an ending: life continuing, something new taking root, and the quiet protection of the life I was moving towards." },
      { t: "p", x: "The circle closes, while the things worth carrying continue with us." },
      { t: "pull", x: "Closing a circle is not only about leaving something behind.\nIt is also about protecting what is beginning." },
    ],
  },
  no: {
    eyebrow: "En fullendt sirkel",
    title: "Kransen",
    sub: "Noen ting tar vi vare på til historien de hører hjemme i, er klar til å lukke seg.",
    imageCaption: "Kransen, med detaljer fra sirkelen.",
    cta: "Start en bestilling",
    imageAlt: "Kransen av kvist og rognbær med et halvmalt gevir, og detaljer av gresshoppen, malte blomster og skjellglasset",
    blocks: [
      { t: "p", x: "Denne kransen er laget av fragmenter fra et kapittel av mitt eget liv." },
      {
        t: "p",
        x: "Dunkjevlen bærer minnet om Lucy — katten som kom inn i livet mitt i løpet av ekteskapet mitt. En liten, nesten hverdagslig ting, men for meg bærer den en hel tid, et hjem, et forhold, og livet som fantes rundt den.",
      },
      {
        t: "p",
        x: "Geviret fant jeg i Finnmark i 2023.\nJeg kunne ikke ha visst den gangen hva det til slutt skulle bli en del av. Men den reisen har en helt spesiell plass i historien min. Det var der jeg først forsto — ikke som en tanke, men som noe mye dypere — at ekteskapet mitt kom til å ta slutt. Jeg husker det nesten som et budskap som kom før jeg var klar til å handle på det.",
      },
      { t: "p", x: "Jeg bar geviret hjem.\nÅr senere malte jeg det og ga det en plass i dette verket." },
      {
        t: "p",
        x: "Og så er det den store grønne gresshoppen — et symbol som kom inn i livet mitt og bar på sin egen betydning av endring, bevegelse og motet til å gå videre.",
      },
      { t: "pull", x: "Hver for seg bærer disse fragmentene ulike øyeblikk.\nSammen, inne i kransens sirkel, forteller de én historie." },
      { t: "img" },
      {
        t: "p",
        x: "Et forhold.\nEt liv levd innenfor det.\nDet første visset om at det ville ta slutt.\nForvandlingen som fulgte.\nOg til slutt: at sirkelen lukket seg.",
      },
      { t: "p", x: "Kanskje er det derfor kransen måtte være en sirkel." },
      {
        t: "p",
        x: "Ikke fordi fortiden forsvinner, og ikke fordi alt som hørte den til må kastes.\nNoen ting får lov til å bli igjen.\nMen de kan samles på en annen måte.\nOmstokkes.\nTolkkes på nytt.\nFå en annen form.",
      },
      { t: "p", x: "Rognbærene bringer en ny betydning inn i sirkelen.\nRogn har lenge vært forbundet med beskyttelse — med å vokte hjemmet, terskelen og de som passerer gjennom tider av endring. I dette verket ser jeg bærene som noe mer personlig også." },
      { t: "p", x: "De er den levende delen av kransen.\nSmå, lyse frø holdt mellom fragmentene av et liv som allerede er levd. For meg har de kommet til å representere det som blir igjen etter en slutt: livet som fortsetter, noe nytt som slår rot, og den stille beskyttelsen av livet jeg var på vei mot." },
      { t: "p", x: "Sirkelen lukker seg, mens det som er verdt å bære videre, fortsetter med oss." },
      { t: "pull", x: "Å lukke en sirkel handler ikke bare om å legge noe bak seg.\nDet handler også om å beskytte det som begynner." },
    ],
  },
};

export default function WreathPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const t = STORY[lang];

  useEffect(() => {
    document.title = "The Wreath — The Things We Keep";
    return () => {
      document.title =
        lang === "no"
          ? "The Things We Keep — Gjenstander med en historie"
          : "The Things We Keep — Objects with a story";
    };
  }, [lang]);

  return (
    <div className="bg-paper pb-24 pt-32 sm:pb-32 sm:pt-40" data-testid="wreath-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="ttwk-label" data-testid="wreath-eyebrow">
                {t.eyebrow}
              </p>
              <h1 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-7xl" data-testid="wreath-title">
                {t.title}
              </h1>
              <p className="mt-6 max-w-xl font-serif text-2xl font-light italic text-terracotta sm:text-3xl">{t.sub}</p>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 max-w-2xl">
          {t.blocks.map((b, i) => {
            if (b.t === "img") {
              return (
                <Reveal key={i}>
                  <div className="img-frame my-16 overflow-hidden rounded-sm border border-clay/60">
                    <img src="/media/wreath.jpg" alt={t.imageAlt} className="w-full object-cover" data-testid="wreath-image" />
                  </div>
                  <p className="-mt-10 font-serif text-sm italic text-umber/60">{t.imageCaption}</p>
                </Reveal>
              );
            }
            if (b.t === "pull") {
              return (
                <Reveal key={i}>
                  <p className="my-14 text-center font-serif text-3xl font-light italic leading-snug text-terracotta sm:text-4xl">
                    {b.x.split("\n").map((line, j) => (
                      <span key={j}>
                        {j > 0 && <br />}
                        {line}
                      </span>
                    ))}
                  </p>
                </Reveal>
              );
            }
            return (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.15)}>
                <p className="mt-8 font-serif text-xl font-light leading-relaxed text-umber first:mt-0 sm:text-2xl">
                  {b.x.split("\n").map((line, j) => (
                    <span key={j}>
                      {j > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "commission" } })}
            className="mt-16 rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
            data-testid="wreath-cta"
          >
            {t.cta}
          </button>
        </Reveal>
      </div>
    </div>
  );
}
